import type { AdminUser } from "@features/users/api";
import { getPrimaryContact } from "@shared/directoryUsers";
import EnterpriseDrawer from "@shared/ui/enterprise-drawer";
import { Button, Descriptions, Popconfirm, Skeleton, Space, Tag, Typography } from "antd";

interface DirectoryUserPreviewProps {
  open: boolean;
  user?: AdminUser;
  onClose: () => void;
  onEdit?: (user: AdminUser) => void;
  onDelete?: (user: AdminUser) => void;
  deleting?: boolean;
}

const renderObjects = (user?: AdminUser) => {
  if (!user?.objects || user.objects.length === 0) {
    return "—";
  }
  return (
    <Space wrap>
      {user.objects.map((link) => (
        <Tag key={`${link.region_id}:${link.object_id}`} color="blue">
          Объект {link.object_id}
        </Tag>
      ))}
    </Space>
  );
};

const DirectoryUserPreview = ({
  open,
  user,
  onClose,
  onEdit,
  onDelete,
  deleting,
}: DirectoryUserPreviewProps) => (
  <EnterpriseDrawer
    open={open}
    onClose={onClose}
    title="Карта пользователя"
    size="middle"
    copyUrl={user?.id ? `/admin/users/${encodeURIComponent(user.id)}` : undefined}
    actions={
      <>
        <Button onClick={onClose}>Закрыть</Button>
        {onDelete && (
          <Popconfirm
            title="Удалить пользователя?"
            description="Пользователь будет удален. Это действие нельзя отменить."
            onConfirm={() => user && onDelete(user)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Button danger loading={deleting} disabled={!user}>
              Удалить
            </Button>
          </Popconfirm>
        )}
        <Button type="primary" onClick={() => user && onEdit?.(user)} disabled={!user}>
          Редактировать
        </Button>
      </>
    }
  >
    {!user ? (
      <Skeleton active paragraph={{ rows: 6 }} />
    ) : (
      <Descriptions column={1} size="small" colon={false}>
        <Descriptions.Item label="ID">
          <Typography.Text strong>{user.id}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="ФИО">
          {[user.last_name, user.first_name, user.middle_name].filter(Boolean).join(" ") || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Email">
          {getPrimaryContact(user, "email") ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Телефон">
          {getPrimaryContact(user, "phone") ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Компания">{user.company ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Город">{user.city ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Примечание">{user.note ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Привязанные объекты">{renderObjects(user)}</Descriptions.Item>
        <Descriptions.Item label="Роль">
          {(() => {
            const role = (user.role ?? "user").toLowerCase();
            const roleMap: Record<string, { color: string; label: string }> = {
              admin: { color: "purple", label: "Администратор" },
              user: { color: "blue", label: "Пользователь" },
              viewer: { color: "default", label: "Просмотр" },
            };
            const item = roleMap[role] ?? { color: "default", label: role };
            return <Tag color={item.color}>{item.label}</Tag>;
          })()}
        </Descriptions.Item>
        <Descriptions.Item label="Статус">
          {(() => {
            const status = (user.status ?? "pending").toLowerCase();
            const statusMap: Record<string, { color: string; label: string }> = {
              active: { color: "green", label: "Активен" },
              pending: { color: "blue", label: "Ожидает" },
              blocked: { color: "red", label: "Заблокирован" },
            };
            const item = statusMap[status] ?? { color: "default", label: status };
            return <Tag color={item.color}>{item.label}</Tag>;
          })()}
        </Descriptions.Item>
      </Descriptions>
    )}
  </EnterpriseDrawer>
);

export default DirectoryUserPreview;
